import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Facebook } from "lucide-react";
import { API, BUSINESS, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", phone: "", service: "custom_home", city: "", message: "" };

const ContactLine = ({ icon: Icon, children, href, testId }) => (
  <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testId} className="flex items-start gap-3 text-[#0E1F3F] transition-colors hover:text-[#A67C00]">
    <Icon size={18} className="mt-0.5 shrink-0 text-[#A67C00]" /><span className="text-sm font-medium">{children}</span>
  </a>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Thanks! We'll reach out shortly.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="estimate" className="py-16 sm:py-24 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-3">Free estimate</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Let's talk about your project.</h2>
          <p className="mt-5 leading-relaxed text-[#4A4742]">Send us the details and we'll get back to you to schedule a visit. Prefer the phone? Call us directly.</p>
          <div className="mt-8 space-y-4">
            <ContactLine icon={Phone} href={BUSINESS.phoneHref} testId="contact-phone">{BUSINESS.phone}</ContactLine>
            <ContactLine icon={Mail} href={`mailto:${BUSINESS.email}`} testId="contact-email">{BUSINESS.email}</ContactLine>
            <ContactLine icon={MapPin} testId="contact-address">{BUSINESS.address}</ContactLine>
            <ContactLine icon={Facebook} href={BUSINESS.facebook} testId="contact-facebook">Follow us on Facebook</ContactLine>
          </div>
        </div>
        <form onSubmit={submit} data-testid="estimate-request-form" className="grid gap-4 rounded-2xl border border-[#E6E2DA] bg-white p-6 shadow-sm sm:grid-cols-2 sm:p-8 lg:col-span-7">
          {done && <p data-testid="estimate-success" className="rounded-lg bg-green-50 p-3 text-sm font-medium text-green-700 sm:col-span-2">Request received — we'll be in touch soon.</p>}
          <Input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="lead-name-input" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="lead-email-input" />
          <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="lead-phone-input" />
          <Input placeholder="City (e.g. Apple Valley)" value={form.city} onChange={set("city")} data-testid="lead-city-input" />
          <div className="sm:col-span-2">
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="lead-service-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom_home">Custom home</SelectItem>
                <SelectItem value="concrete">Custom concrete work</SelectItem>
                <SelectItem value="other">Something else</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website || ""} onChange={set("website")} />
          <Textarea className="min-h-32 sm:col-span-2" placeholder="Tell us about your project — size, location, timeline…" value={form.message} onChange={set("message")} data-testid="lead-message-input" />
          <button disabled={sending} data-testid="estimate-submit-btn" className="rounded-full bg-[#F2B91C] px-6 py-3.5 text-sm font-semibold text-[#0E1F3F] transition-colors hover:bg-[#D9A30F] disabled:opacity-60 sm:col-span-2">
            {sending ? "Sending…" : "Request my free estimate"}
          </button>
        </form>
      </div>
    </section>
  );
};
